// src/components/reporting/cohort/CoachCohortReports.js - Coach view: cohorts -> users -> assessments -> detail
// Lists the case-study assessments (report_type 'real_assessment') belonging to
// members of the coach's cohorts. Deliberately not a list of all conversations.
//
// Two endpoints, in two different routers, each the best existing owner of its job:
//   list   GET /api/v1/analytics/assessments?userId=       (assessments.js; Coach branch
//          filters by the assessment's *subject*, pins the tenant, completed only)
//   detail GET /api/v1/analytics/reports/assessments/:id   (analytics.js; authorized by
//          canViewContent — owner, owner's coach, and the super-roles)
// Assessment bodies are never served from the list router. Roleplay debriefs
// (report_type 'roleplay_debrief') are a separate surface and are not shown here.
// 
// Styled to match the User Activity Report (components/reporting/activity/*, styles/activityReporting.css).
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { CaretRight } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useApiCall } from '../../../hooks/useApiCall';
import apiService from '../../../services/apiService';
import { BasicDebriefContent } from '../activity/DebriefContent';
import CohortMultiSelect from './CohortMultiSelect';
import CohortCribSheet from './CohortCribSheet';
import CohortSummaryPage from './CohortSummaryPage';
import '../../../styles/roleplay.css';
import '../../../styles/assessment.css';
import '../../../styles/activityReporting.css';

const formatDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString();
};

const userLabel = (u) => u.displayName || u.email || '';

const CoachCohortReports = ({ tenant, breadcrumbs = [] }) => {
  const { user } = useAuth();
  const { loading, error, makeRequest, clearError } = useApiCall();

  const [cohorts, setCohorts] = useState([]);
  const [selectedCohortIds, setSelectedCohortIds] = useState([]);
  const [viewingUsers, setViewingUsers] = useState(false);
  const [viewingCribSheet, setViewingCribSheet] = useState(false);
  const [viewingCohortSummary, setViewingCohortSummary] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [assessments, setAssessments] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  const loadCohorts = useCallback(async () => {
    if (!tenant?.id) return;
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/tenants/${tenant.id}/cohorts`, user),
        'Loading your cohorts...'
      );
      if (data.success) setCohorts(data.data.cohorts || []);
    } catch (err) {
      console.error('Error loading cohorts:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id]);

  useEffect(() => {
    loadCohorts();
  }, [loadCohorts]);

  // While the user list is on screen the selector stays in the header, so any
  // change to the selection re-fetches rather than waiting on a button press.
  useEffect(() => {
    if (!viewingUsers) return;
    if (selectedCohortIds.length === 0) {
      setUsers([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await makeRequest(
          () => apiService.get(
            `/api/v1/tenants/${tenant.id}/users?cohortIds=${selectedCohortIds.join(',')}&limit=200`,
            user
          ),
          'Loading users...'
        );
        if (!cancelled && data.success) setUsers(data.data.users || []);
      } catch (err) {
        console.error('Error loading cohort users:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewingUsers, selectedCohortIds, tenant?.id]);

  // One group per selected cohort, cohorts A-Z and users A-Z within each. A user
  // in two selected cohorts is listed under both. The trailing 'Other' bucket
  // only appears if a returned user carries no active membership in the
  // selection (e.g. their cohort was deactivated between the two queries).
  const groupedUsers = useMemo(() => {
    const groups = new Map();
    const push = (key, name, u) => {
      if (!groups.has(key)) groups.set(key, { key, name, users: [] });
      groups.get(key).users.push(u);
    };
    users.forEach((u) => {
      const matched = (u.cohorts || []).filter((c) => selectedCohortIds.includes(String(c.id)));
      if (matched.length === 0) push('__other', 'Other', u);
      else matched.forEach((c) => push(String(c.id), c.name || 'Cohort', u));
    });
    const all = [...groups.values()];
    const named = all.filter((g) => g.key !== '__other').sort((a, b) => a.name.localeCompare(b.name));
    const other = all.filter((g) => g.key === '__other');
    const ordered = [...named, ...other];
    ordered.forEach((g) => g.users.sort((a, b) => userLabel(a).localeCompare(userLabel(b))));
    return ordered;
  }, [users, selectedCohortIds]);

  const loadAssessments = async (targetUser) => {
    setSelectedUser(targetUser);
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/analytics/assessments?userId=${targetUser.id}`, user),
        'Loading assessments...'
      );
      if (data.success) setAssessments(data.data.reports || []);
    } catch (err) {
      console.error('Error loading assessments:', err);
    }
  };

  const loadReport = async (assessmentSummary) => {
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/analytics/reports/assessments/${assessmentSummary.id}`, user),
        'Loading assessment...'
      );
      if (data.success) setSelectedReport(data.data);
    } catch (err) {
      console.error('Error loading assessment detail:', err);
    }
  };

  const backToCohorts = () => {
    setViewingUsers(false);
    setViewingCribSheet(false);
    setViewingCohortSummary(false);
    setUsers([]);
    setSelectedUser(null);
    setAssessments(null);
    setSelectedReport(null);
    clearError();
  };

  const backToUsers = () => {
    setSelectedUser(null);
    setAssessments(null);
    setSelectedReport(null);
    clearError();
  };

  const backToAssessments = () => {
    setSelectedReport(null);
    clearError();
  };

  // Build the breadcrumb trail: parent-provided crumbs (Reporting Dashboard),
  // then one entry per drill-down level reached so far. Only the last entry
  // is rendered as the active (non-clickable) crumb.
  const parentCrumbs = breadcrumbs.slice(0, -1);
  const trail = [
    ...parentCrumbs,
    { label: 'Cohort Reporting', onClick: backToCohorts },
  ];
  if (viewingUsers) trail.push({ label: 'Participants', onClick: backToCohorts });
  if (viewingCribSheet) trail.push({ label: 'Cohort Crib Sheet', onClick: backToCohorts });
  if (viewingCohortSummary) trail.push({ label: 'Cohort Real Summary', onClick: backToCohorts });
  if (selectedUser) trail.push({ label: selectedUser.displayName || selectedUser.email, onClick: backToUsers });
  if (selectedReport) trail.push({ label: selectedReport.title || 'Assessment', onClick: backToAssessments });
  trail[trail.length - 1] = { ...trail[trail.length - 1], onClick: undefined };

  const renderBreadcrumb = () => (
    <div className="arp-breadcrumb">
      {trail.map((crumb, index) => (
        <React.Fragment key={index}>
          {index > 0 && <CaretRight size={11} weight="bold" />}
          {crumb.onClick ? (
            <button className="arp-breadcrumb-link" onClick={crumb.onClick}>{crumb.label}</button>
          ) : (
            <span>{crumb.label}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderHeader = (title, subtitle, aside) => (
    <div className="arp-header">
      <div className="arp-header-main">
        <h1 className="arp-title">{title}</h1>
        <p className="arp-subtitle">{subtitle}</p>
      </div>
      {aside && <div className="arp-actions arp-header-aside">{aside}</div>}
    </div>
  );

  // The cohort-level reports are single-cohort only, so their buttons only
  // appear once the selection is exactly one cohort. They travel with the
  // selector onto the participant list so drilling in isn't a one-way door.
  const cohortReportButtons = selectedCohortIds.length === 1 && (
    <>
      <button
        type="button"
        className="arp-btn arp-btn-secondary"
        onClick={() => setViewingCribSheet(true)}
      >
        Cohort Crib Sheet
      </button>
      <button
        type="button"
        className="arp-btn arp-btn-secondary"
        onClick={() => setViewingCohortSummary(true)}
      >
        Cohort Real Summary
      </button>
    </>
  );

  const cohortSelector = (
    <CohortMultiSelect
      cohorts={cohorts}
      selectedIds={selectedCohortIds}
      onChange={setSelectedCohortIds}
      disabled={cohorts.length === 0}
    />
  );

  if (!tenant) {
    return (
      <div className="activity-report-page">
        <div className="arp-error-state">No tenant selected.</div>
      </div>
    );
  }

  // Cohort Crib Sheet — a cohort-level report rather than a step in the
  // participant drill-down, so it sits beside the participant list rather than
  // beneath it. Single-cohort by definition; the button that opens it only
  // exists when the selection is exactly one.
  if (viewingCribSheet && selectedCohortIds.length === 1) {
    const selected = cohorts.find((c) => String(c.id) === selectedCohortIds[0]);
    return (
      <div className="activity-report-page">
        {renderBreadcrumb()}
        <CohortCribSheet cohortId={selectedCohortIds[0]} cohortName={selected?.name} />
      </div>
    );
  }

  // Cohort Real Summary — same single-cohort-only shape as the crib sheet
  // above. targetTenantId is the tenant this whole page is already scoped
  // to, per the tenant-guard contract (see middleware/scopeResolution.js).
  if (viewingCohortSummary && selectedCohortIds.length === 1) {
    const selected = cohorts.find((c) => String(c.id) === selectedCohortIds[0]);
    return (
      <div className="activity-report-page activity-report-page--full-height">
        <CohortSummaryPage
          cohortId={selectedCohortIds[0]}
          cohortName={selected?.name}
          targetTenantId={tenant.id}
          breadcrumb={renderBreadcrumb()}
        />
      </div>
    );
  }

  // Step 4: assessment detail — rendered by the same component RPT-003's
  // ConversationDebriefPage uses for its basic branch. That component expects
  // `debrief` to be the report *body*, while this endpoint returns the whole
  // report, hence the unwrap to `result`.
  if (selectedReport) {
    return (
      <div className="activity-report-page">
        {renderBreadcrumb()}
        {renderHeader(
          selectedReport.title || 'Assessment',
          'Read-only — no edits are made from this view.',
          <button className="arp-btn arp-btn-secondary" onClick={() => window.print()}>
            Print / Save as PDF
          </button>
        )}
        <BasicDebriefContent
          conversationTitle={selectedReport.title}
          data={{
            caseStudy: selectedReport.caseStudy,
            learningGoal: selectedReport.learningGoal,
            narrative: selectedReport.narrative,
            debrief: selectedReport.result,
          }}
        />
      </div>
    );
  }

  // Step 3: assessment list for a user
  if (selectedUser) {
    return (
      <div className="activity-report-page">
        {renderBreadcrumb()}
        {renderHeader(
          selectedUser.displayName || selectedUser.email,
          'Completed case study assessments for this participant'
        )}

        {error && <div className="arp-error-state">{error}</div>}

        {loading ? (
          <div className="arp-state">Loading…</div>
        ) : assessments && assessments.length === 0 ? (
          <div className="arp-state">No completed assessments yet for this participant.</div>
        ) : assessments && assessments.length > 0 ? (
          <div className="arp-table-wrapper">
            <table className="arp-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {assessments.map(assessment => (
                  <tr key={assessment.id}>
                    <td>
                      <button className="arp-row-link" onClick={() => loadReport(assessment)}>
                        {assessment.title || 'Assessment'}
                      </button>
                    </td>
                    <td className="arp-cell-muted">{formatDate(assessment.createdAt)}</td>
                    <td className="arp-cell-muted">{assessment.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    );
  }

  // Step 2: participant list for selected cohorts — the selector stays in the
  // header so the selection can be changed without going back a level.
  if (viewingUsers) {
    return (
      <div className="activity-report-page">
        {renderBreadcrumb()}
        {renderHeader(
          'Participants',
          `${users.length} participant${users.length === 1 ? '' : 's'} in the selected cohort${selectedCohortIds.length === 1 ? '' : 's'}`,
          cohortSelector
        )}

        {cohortReportButtons && (
          <div className="arp-button-row arp-button-row-standalone">{cohortReportButtons}</div>
        )}

        {error && <div className="arp-error-state">{error}</div>}

        {loading ? (
          <div className="arp-state">Loading…</div>
        ) : selectedCohortIds.length === 0 ? (
          <div className="arp-state">Select at least one cohort to see its participants.</div>
        ) : users.length === 0 ? (
          <div className="arp-state">No participants found in the selected cohort(s).</div>
        ) : (
          <div className="arp-table-wrapper">
            <table className="arp-table">
              <thead>
                <tr>
                  <th>Participant</th>
                  <th>Email</th>
                  <th>Status</th>
                </tr>
              </thead>
              {groupedUsers.map(group => (
                <tbody key={group.key}>
                  <tr className="arp-group-row">
                    <th colSpan={3} scope="colgroup">
                      {group.name}
                      <span className="arp-group-count">
                        {group.users.length} participant{group.users.length === 1 ? '' : 's'}
                      </span>
                    </th>
                  </tr>
                  {group.users.map(u => (
                    <tr key={`${group.key}-${u.id}`}>
                      <td>
                        <button className="arp-row-link" onClick={() => loadAssessments(u)}>
                          {userLabel(u)}
                        </button>
                      </td>
                      <td className="arp-cell-muted">{u.email}</td>
                      <td className="arp-cell-muted">{u.status}</td>
                    </tr>
                  ))}
                </tbody>
              ))}
            </table>
          </div>
        )}
      </div>
    );
  }

  // Step 1: cohort selection
  return (
    <div className="activity-report-page">
      {renderBreadcrumb()}
      {renderHeader(
        'Cohort Reporting',
        `Select one or more cohorts in ${tenant.name}`,
        cohortSelector
      )}

      {error && <div className="arp-error-state">{error}</div>}

      {loading ? (
        <div className="arp-state">Loading…</div>
      ) : cohorts.length === 0 ? (
        <div className="arp-state">You are not assigned to any cohorts yet. Contact your administrator to be assigned to a cohort.</div>
      ) : (
        <div className="arp-button-row">
          <button
            onClick={() => setViewingUsers(true)}
            className="arp-btn arp-btn-primary"
            disabled={selectedCohortIds.length === 0 || loading}
          >
            View Participants
          </button>
          {cohortReportButtons}
        </div>
      )}
    </div>
  );
};

CoachCohortReports.propTypes = {
  tenant: PropTypes.object.isRequired,
  breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    active: PropTypes.bool
  }))
};

export default CoachCohortReports;
